import { useMemo } from 'react';
import get from 'lodash/get';
import has from 'lodash/has';
import isEmpty from 'lodash/isEmpty';
import { di } from 'react-magnetic-di';
import { useAdjustedFields } from '@atlassian/ui-modifications-core/controllers';
import type { AdjustedFieldValue, AdjustedField } from '../../../common/types';
import type {
	CheckboxGroupState,
	FieldState,
	SelectState,
} from 'state/persisted/request-create/types';
const pickPropertiesToOverride = (field: AdjustedField): Partial<FieldState> => {
	const overrideProperties = {
		fieldName: 'label',
		description: 'description',
		descriptionRaw: 'description',
		isReadOnly: 'disabled',
		isRequired: 'required',
		isVisible: 'displayed',
		value: 'valueOverride',
		// For options visibility, field like select uses options.
		options: 'options',
		// For options visibility, field like radio uses items.
		items: 'items',
	} satisfies Partial<
		Record<
			keyof AdjustedField | `value.${keyof AdjustedFieldValue}`,
			keyof FieldState | keyof CheckboxGroupState | keyof SelectState
		>
	>;

	return Object.entries(overrideProperties).reduce(
		(acc: Record<string, unknown>, [uimProperty, jsmProperty]) => {
			if (has(field, uimProperty)) {
				acc[jsmProperty] = get(field, uimProperty);
			}

			return acc;
		},
		{} as Partial<FieldState>,
	);
};

export const useFieldsOverrides = ({ fields }: { fields: FieldState[] }) => {
	di(useAdjustedFields);
	const [overrides] = useAdjustedFields();

	return useMemo(() => {
		if (!overrides) {
			return { fields };
		}

		const overridenFields = fields.map((field: FieldState) => {
			const fieldOverrides = overrides[field.id];
			if (!fieldOverrides) {
				return field;
			}

			const supportedOverrides = pickPropertiesToOverride(fieldOverrides);

			if (isEmpty(supportedOverrides)) {
				return field;
			}

			return {
				...field,
				...supportedOverrides,
			} as FieldState;
		});

		return { fields: overridenFields };
	}, [overrides, fields]);
};
